import { FunctionComponent } from 'react';
import { Flex, Box, Text } from '@rebass/emotion';
import { useQuery } from '@apollo/client';
import Loader from '@app/components/Loader';
import LoginLink from '@app/components/LoginLink';
import { FormattedMessage } from 'react-intl';
import useLocaleWithFallback from '@app/shop/hooks/useLocaleWithFallback';

import { GET_BASE_PRODUCTS_MESSAGES } from './ProductUpgradeMessage.queries';
import Icon from './icons';
import { GetBaseProductsMessages } from './__generated__/GetBaseProductsMessages';
import messages from './ProductUpgradeMessage.messages';

const BaseProductMessage: FunctionComponent<{
  sku?: string | null;
  customerEmail?: string;
}> = ({ sku, customerEmail }) => {
  const {
    findTranslationByKeyWithFallback,
    getLanguagesWithFallback,
  } = useLocaleWithFallback();
  const { data, loading } = useQuery<GetBaseProductsMessages>(
    GET_BASE_PRODUCTS_MESSAGES,
    {
      skip: !sku,
      variables: {
        sku,
        languages: getLanguagesWithFallback(),
      },
    }
  );

  if (loading) {
    return <Loader />;
  }

  const baseProductMessages =
    data?.content?.required_base_products?.[0]?.messages;

  if (customerEmail && !baseProductMessages) {
    return null;
  }

  const MessageIcon = () => (
    <Box mr={2}>
      <Icon
        width={25}
        type={
          baseProductMessages
            ? 'exclamation-mark-yellow'
            : 'exclamation-mark-blue'
        }
      />
    </Box>
  );

  const requiredBaseProducts = findTranslationByKeyWithFallback({
    translations: baseProductMessages,
    key: 'required_base_products',
  });

  const Message = ({ isAnonymous }: { isAnonymous?: boolean }) => (
    <Box mb={4}>
      <Box
        mb={1}
        dangerouslySetInnerHTML={{
          __html: requiredBaseProducts,
        }}
      />
      {isAnonymous && (
        <Text>
          <FormattedMessage {...messages.ownershipWarning} />
        </Text>
      )}
    </Box>
  );

  return (
    <Flex
      data-testid={
        customerEmail
          ? 'upgrade-message-no-qualifying-products'
          : 'upgrade-message-anonymous'
      }
    >
      <MessageIcon />
      <Box flex={1}>
        {requiredBaseProducts && <Message isAnonymous={!customerEmail} />}

        {customerEmail ? (
          <FormattedMessage
            {...messages.noQualifyingProducts}
            values={{ customerEmail }}
          />
        ) : (
          <FormattedMessage
            {...messages.loginMessage}
            values={{
              LoginLink: (
                <LoginLink>
                  <FormattedMessage {...messages.loginLink} />
                </LoginLink>
              ),
            }}
          />
        )}
      </Box>
    </Flex>
  );
};

export default BaseProductMessage;
